import { ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { useEffect, useRef, type ReactNode } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { ModalPortal } from './ModalPortal';
import { Alert01Icon, CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Optional body copy. Omit when the children (e.g. a findings table)
   * carry the whole message. Additive — existing consumers unchanged. */
  message?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  destructive?: boolean;
  loading?: boolean;
  /** Optional content between the message and the buttons (e.g. a selectable
   * findings table). Additive — existing consumers are unchanged. */
  children?: ReactNode;
  /** Disable the confirm button (e.g. zero rows selected). */
  confirmDisabled?: boolean;
  /** Card width override (default 'max-w-sm'). Additive — existing consumers unchanged. */
  maxWidthClass?: string;
  /** Hide the Cancel button for acknowledge-only states (e.g. a dead-link
   * notice) where a Cancel twin next to the confirm would be two buttons that
   * both dismiss. Initial focus moves to the confirm button. Additive;
   * existing consumers unchanged. */
  hideCancel?: boolean;
  /** Kept beside the actions, even when long form content scrolls. */
  error?: string | null;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  onConfirm,
  onCancel,
  destructive = false,
  loading = false,
  children,
  confirmDisabled = false,
  maxWidthClass = 'max-w-sm',
  hideCancel = false,
  error = null,
}: ConfirmDialogProps) {
  // Initial focus: a DESTRUCTIVE confirm focuses Cancel (no default Yes on a
  // dangerous action, NN/g); an additive one focuses its primary, because the
  // user already chose it (the re-scan email's "Add N issues" is that exact
  // button, and Enter used to cancel it; 2026-09-09 teardown). A primary that
  // is disabled at open cannot take focus, so Cancel keeps it there; with
  // Cancel hidden the confirm was always the target. Keyboard users are never
  // dropped outside the dialog in any branch.
  const primaryFirst = hideCancel || (!destructive && !confirmDisabled);
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) initialFocusRef.current?.focus();
  }, [open]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(open, dialogRef, onCancel);

  if (!open) return null;

  // Portal to document.body so the fixed overlay escapes any ancestor stacking
  // context. Rendered inline, a caller inside a `position: sticky`/`transform`
  // ancestor (e.g. DevResetButton in the sticky <aside> sidebar) traps this
  // z-50 overlay in that context, painting it beneath sibling content so the
  // dialog is invisible and unclickable. See ConfirmDialog.portal.test.tsx.
  //
  // This dialog is where that bug was found and fixed first; ModalPortal is
  // that fix generalised to every dashboard modal. Routed through it so the
  // rule has exactly one implementation and this file cannot drift from the
  // others it now shares a guard with.
  return (
    <ModalPortal>
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-labelledby="confirm-title"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className={`bg-white rounded-xl w-full ${maxWidthClass} shadow-xl overflow-hidden max-h-[calc(100dvh-2rem)] flex flex-col`}>
        {/* Header */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between shrink-0">
          <ModalTitle icon={destructive ? Alert01Icon : CheckmarkCircle01Icon} id="confirm-title" className="oq-dialog__title">{title}</ModalTitle>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pt-4 overflow-y-auto">
          {message ? <p className="text-sm text-gray-600 mb-5">{message}</p> : null}
          {children}
        </div>
        <div className="px-6 pb-5 pt-3 shrink-0">
          {error ? <p role="alert" className="mb-3 text-sm text-red-600">{error}</p> : null}
          <div className="flex gap-3 justify-end">
            {!hideCancel && (
              <button
                ref={primaryFirst ? undefined : initialFocusRef}
                type="button"
                className="btn-secondary"
                onClick={onCancel}
                disabled={loading}
              ><ModalAction kind="cancel">
                Cancel
              </ModalAction></button>
            )}
            <button
              ref={primaryFirst ? initialFocusRef : undefined}
              type="button"
              className={destructive ? 'btn-danger' : 'btn-primary'}
              onClick={onConfirm}
              disabled={loading || confirmDisabled}
            ><ModalAction kind={destructive ? "delete" : "done"}>
              {loading ? 'Processing...' : confirmLabel}
            </ModalAction></button>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
